import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ApiResponse } from '@shared/interfaces/responses/ApiResponse.interface';
import { FORM_ERROR_KEY, FormUtils } from '@shared/utils/form-utils';
import { StringUtils } from '@shared/utils/string-utils';

type ErrorMessage = Record<string, string | string[]>;

@Injectable({
  providedIn: 'root',
})
export class FormErrorService {
  component: FormComponentInterface;
  form: UntypedFormGroup;

  register(component: FormComponentInterface): void {
    this.component = component;
    this.component.errorMessage = {};
    this.component.formErrorKey = FORM_ERROR_KEY;

    this.form = this.component.getForm();

    if (this.form) {
      this.form?.valueChanges.subscribe(() => {
        FormUtils.removeError(this.form, 'formError');
        this.component.isSubmitted = false;

        this.component.errorMessage[FORM_ERROR_KEY] = null;
      });

      for (const key of Object.keys(this.form.controls)) {
        const control = this.form.controls[key];
        control.valueChanges.subscribe(() => {
          FormUtils.removeError(control, 'jip_' + key);
          this.component.errorMessage[key] = null;
        });
      }
    }
  }

  handleError(res: HttpErrorResponse | ApiResponse): string {
    if (!res) {
      return '';
    }

    const errorMessage = FormUtils.getErrorObject(res);

    if (Object.hasOwn(errorMessage, FORM_ERROR_KEY)) {
      this.form?.setErrors({ [FORM_ERROR_KEY]: true });
    } else {
      this.handleFieldErrors(errorMessage);
    }

    this.component.errorMessage = errorMessage;

    return this.formatErrorMessages(errorMessage);
  }

  private handleFieldErrors(errorMessage: ErrorMessage): void {
    const found = this.processFieldErrors(errorMessage);

    if (!found) {
      this.form?.setErrors({ [FORM_ERROR_KEY]: true });
    }
  }

  private processFieldErrors(errorMessage: ErrorMessage): boolean {
    return Object.keys(errorMessage).some(key => {
      if (!key) return false;

      const message = errorMessage[key];
      const lowerKey = StringUtils.toLowerCaseFirstLetter(key);
      const control = this.form?.get(lowerKey);

      if (!control) return false;

      control.setErrors({ ['jip_' + lowerKey]: true });

      if (this.component.errorMessage) {
        this.component.errorMessage[lowerKey] =
          typeof message === 'string' ? message : message.join(', ');
      }

      return true;
    });
  }

  private formatErrorMessages(errorMessage: ErrorMessage): string {
    const returnErrorArr = Object.values(errorMessage)?.map(
      (errorData: string | string[]) => {
        return typeof errorData === 'object' && Array.isArray(errorData)
          ? errorData.join(', ')
          : errorData;
      }
    );

    return returnErrorArr?.join(' - ');
  }
}
export interface FormComponentInterface {
  formErrorKey: string;
  errorMessage: Record<string, string>;
  isSubmitting?: boolean;
  isSubmitted?: boolean;
  getForm(): UntypedFormGroup;
}
